import { useEffect, useState } from 'react';
import { pushApplyVoucherToDataLayer } from '../analytics';
import { fetchVoucherCampaigns } from '../utils/voucherCampaignUtils';
import { getShoppingBasketFromLocalStorage, handleShoppingBasketUpdate, parseBasketJson } from './useShoppingBasket';
import type { CampaignContext } from '../../generated/api/campaignContext';
import type { CampaignPromotion } from '../../generated/api/campaignPromotion';
import type { OnlineModel } from '../../generated/api/onlineModel';
import type { ShoppingBasketType } from '../types/shoppingBasket';

export interface UseVoucherHookResult {
  selectedVoucherCode: string;
  voucherError: boolean;
  isLoadingModels: boolean;
  updatedModels: OnlineModel[];
  applyVoucher: (voucherCode: string) => Promise<void>;
  clearVoucher: () => Promise<void>;
  setVoucherError: React.Dispatch<React.SetStateAction<boolean>>;
  campaignsFromVoucher: CampaignPromotion[];
  campaignContextsFromVoucher: CampaignContext[];
}

export const useVoucher = (
  loadVoucherModels: (code: string, guids: string[]) => Promise<{ models: OnlineModel[] }>,
  setActiveVoucher: (code: string) => void,
  fetchModelsByCodes: (onlineModelCodes: string[]) => void,
  shoppingBasket: ShoppingBasketType,
  activeVoucher?: string,
  voucherOnlineModels?: OnlineModel[]
): UseVoucherHookResult => {
  const [selectedVoucherCode, setSelectedVoucherCode] = useState<string>(activeVoucher || '');
  const [voucherError, setVoucherError] = useState(false);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [updatedModels, setUpdatedModels] = useState<OnlineModel[]>(voucherOnlineModels || []);
  const [campaignsFromVoucher, setCampaignsFromVoucher] = useState<CampaignPromotion[]>([]);
  const [campaignContextsFromVoucher, setCampaignContextsFromVoucher] = useState<CampaignContext[]>([]);

  // Sync selectedVoucherCode when activeVoucher changes (e.g. voucher added in another tab)
  useEffect(() => {
    const newVoucher = activeVoucher ?? '';
    if (newVoucher !== selectedVoucherCode) {
      setSelectedVoucherCode(newVoucher);
    }
  }, [activeVoucher, selectedVoucherCode]);

  const fetchCampaignsForModels = async (voucherCode: string, onlineModelCodes: string[]) => {
    const { campaignContexts, campaignPromotions } = await fetchVoucherCampaigns(voucherCode, onlineModelCodes);
    setCampaignsFromVoucher(campaignPromotions);
    setCampaignContextsFromVoucher(campaignContexts);
  };

  // Fetch campaigns when voucher + models are available (covers both mount and async reload cases)
  const voucherModelsLength = voucherOnlineModels?.length ?? 0;
  useEffect(() => {
    if (activeVoucher && voucherModelsLength > 0) {
      const onlineModelCodes = voucherOnlineModels!.map(m => m.onlineModelCode);
      fetchCampaignsForModels(activeVoucher, onlineModelCodes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVoucher, voucherModelsLength]);

  const applyVoucher = async (voucherCode: string) => {
    if (!voucherCode) {
      return;
    }

    setIsLoadingModels(true);
    try {
      const onlineModelCodeGuids = [...new Set(shoppingBasket?.items?.map(item => item.guid))];
      const response = await loadVoucherModels(voucherCode, onlineModelCodeGuids);
      setVoucherError(false);
      setActiveVoucher(voucherCode);
      setSelectedVoucherCode(voucherCode);
      setUpdatedModels(response?.models ?? []);
      pushApplyVoucherToDataLayer(voucherCode);
      const basket = parseBasketJson(getShoppingBasketFromLocalStorage());
      basket.vouchers = [voucherCode];
      await handleShoppingBasketUpdate(JSON.stringify(basket), false);
      await fetchCampaignsForModels(voucherCode, onlineModelCodeGuids);
    } catch (err) {
      setVoucherError(true);
      setActiveVoucher('');
      setSelectedVoucherCode('');
      const basket = parseBasketJson(getShoppingBasketFromLocalStorage());
      basket.vouchers = [];
      await handleShoppingBasketUpdate(JSON.stringify(basket), false);
      throw err;
    } finally {
      setIsLoadingModels(false);
    }
  };

  const clearVoucher = async () => {
    setActiveVoucher('');
    setSelectedVoucherCode('');
    setVoucherError(false);
    setUpdatedModels([]);
    setCampaignsFromVoucher([]);
    setCampaignContextsFromVoucher([]);
    const basket = parseBasketJson(getShoppingBasketFromLocalStorage());
    basket.vouchers = [];
    await handleShoppingBasketUpdate(JSON.stringify(basket), false);
    const onlineModelCodeGuids = [...new Set(shoppingBasket?.items?.map(item => item.guid))];
    fetchModelsByCodes(onlineModelCodeGuids);
  };

  return {
    selectedVoucherCode,
    voucherError,
    isLoadingModels,
    updatedModels,
    applyVoucher,
    clearVoucher,
    setVoucherError,
    campaignsFromVoucher,
    campaignContextsFromVoucher,
  } satisfies UseVoucherHookResult;
};
